import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, MessageSquare, TrendingUp, Award } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';

export default function TrainerRatingsDetail() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileRes = await axios.get(`${API}/trainers/me`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(profileRes.data);
        const reviewsRes = await axios.get(`${API}/trainers/${profileRes.data.id}/reviews`);
        setReviews(reviewsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  // Calculate breakdown
  const avgRating = profile?.rating || 0;
  const totalReviews = profile?.total_reviews || 0;
  const ratingBreakdown = [5, 4, 3, 2, 1].map(r => ({
    stars: r,
    count: reviews.filter(rev => Math.round(rev.overall_rating || 0) === r).length,
  }));

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/trainer/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">Ratings & Reviews</h1>
          <p className="text-slate-500 mt-1">See what companies say about your training sessions</p>
        </div>

        {/* Rating Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="font-outfit text-5xl font-bold text-slate-900 mb-2">{avgRating.toFixed(1)}</div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`w-5 h-5 ${s <= Math.round(avgRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
              ))}
            </div>
            <p className="text-sm text-slate-500">Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 col-span-2">
            <h3 className="font-semibold text-slate-900 mb-4">Rating Distribution</h3>
            <div className="space-y-2">
              {ratingBreakdown.map(r => (
                <div key={r.stars} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-600 w-6">{r.stars}</span>
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div className="bg-amber-400 h-full rounded-full transition-all" style={{ width: `${totalReviews > 0 ? (r.count / totalReviews) * 100 : 0}%` }} />
                  </div>
                  <span className="text-sm text-slate-500 w-8 text-right">{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">All Reviews ({reviews.length})</h3>
          </div>
          {reviews.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">No reviews yet</h4>
              <p className="text-slate-500">Complete trainings to receive your first review</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {reviews.map((review, idx) => (
                <div key={idx} className="p-5 hover:bg-slate-50 transition-colors" data-testid={`review-${idx}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-medium text-slate-900">{review.corporate_name || 'Company'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{review.job_title || 'Training'} - {new Date(review.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= Math.round(review.overall_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                      ))}
                      <span className="ml-1 text-sm font-medium text-slate-600">{(review.overall_rating || 0).toFixed(1)}</span>
                    </div>
                  </div>
                  {/* Criteria breakdown */}
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    {[
                      { label: 'Knowledge', key: 'knowledge_rating' },
                      { label: 'Communication', key: 'communication_rating' },
                      { label: 'Engagement', key: 'engagement_rating' },
                      { label: 'Material', key: 'material_quality_rating' },
                    ].map(c => (
                      <div key={c.key} className="text-center">
                        <div className="text-xs text-slate-500">{c.label}</div>
                        <div className="text-sm font-medium text-slate-700">{review[c.key]?.toFixed(1) || '-'}/5</div>
                      </div>
                    ))}
                  </div>
                  {review.comment && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{review.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
